import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Card, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import { BakeasyButton } from '../../components/buttons/BakeasyButton';
import { useTranslation } from 'react-i18next';
import orderezlogo from '../../assets/orderezlogo.jpg';
import { BakeasySelect } from '../../components/dropdowns/BakeasySelect';
import {
  findLanguageCode,
  findLanguageName,
  useLocalStorage,
} from '../../languages/LanguageUtils';
import i18n from '../../languages/languages';
import { LANGUAGES } from '../../constants/Constants';
import { useAppDispatch } from '../../redux_store/reduxStore';
import { authActions } from '../../redux_store/slices/authSlice';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import BakeasyLinkButton from '../../components/buttons/BakeasyLinkButton';
import {
  setLoginInfo,
  clearLoginInfo,
  getLoginInfo,
} from '../../utils/LocalStorageUtil';
import BakeasyCheckbox from '../../components/common/BakeasyCheckbox';
import env from '../../config/env';
import * as Regex from '../../constants/Regex';
import useLogin from './useLogin';
import useValidation from '../../custom/useValidation';

export const Login: FC = (): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { validateField } = useValidation();
  const { openForgotPasswordDialog } = useLogin();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [emailId, setEmailId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [remember, setRemember] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useLocalStorage(
    'language',
    'en'
  );

  const checkValidCredentials = (): boolean => {
    const isEmailValid = validateField({
      fieldName: 'USERNAME',
      fieldValue: emailId,
      validationPattern: Regex.username,
    });
    const isPasswordValid = validateField({
      fieldName: 'PASSWORD',
      fieldValue: password,
    });

    return isEmailValid && isPasswordValid;
  };

  async function onSubmit(username: any, password: any) {
    try {
      if (checkValidCredentials()) {
        await dispatch(authActions.getUser({ userName: username, password }));
        if (remember) {
          setLoginInfo(username, password, remember);
        } else {
          clearLoginInfo();
        }
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
    }
  }

  function changeLanguage(language: any) {
    setDefaultLanguage(language);
    window.location.reload();
  }

  useEffect(() => {
    i18n.changeLanguage(defaultLanguage);
  }, [defaultLanguage]);

  useEffect(() => {
    const savedLocalStorage = getLoginInfo();
    if (
      savedLocalStorage &&
      !emailId &&
      !password &&
      savedLocalStorage.remember
    ) {
      setEmailId(savedLocalStorage.username);
      setPassword(savedLocalStorage.password);
      setRemember(savedLocalStorage.remember);
    }
  }, [emailId, password]);

  return (
    <Grid>
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <img src={orderezlogo} style={{ width: '150px', margin: '10px' }} />
        <Grid margin={2}>
          <BakeasySelect
            label={t('LANGUAGE')}
            statusOptions={LANGUAGES}
            value={findLanguageName(defaultLanguage)}
            size={'small'}
            onChange={(e: any) => {
              changeLanguage(findLanguageCode(e?.target?.value));
            }}
          />
        </Grid>
      </Grid>
      <Grid container alignItems="center" justifyContent="center" height="75vh">
        <Card
          sx={{
            margin: 1,
            padding: 2,
            borderRadius: 1,
            width: isMobile ? 'auto' : '400px',
          }}
        >
          <Grid container justifyContent="center" padding={1}>
            <Typography
              sx={{ fontWeight: 'bold', fontSize: '1.25rem', marginBottom: 4 }}
            >
              {t('SIGN_IN_PORTAL')}
            </Typography>

            <TextField.Username
              value={emailId}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmailId(event?.target?.value);
              }}
            />

            <Divider sx={{ margin: 1 }} />

            <TextField.Password
              value={password}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(event?.target?.value);
              }}
            />

            <Grid
              container
              justifyContent="space-between"
              marginBottom={2}
              marginTop={2}
            >
              <BakeasyCheckbox
                label={t('REMEMBER_ME')}
                value={remember}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  setRemember(!remember);
                }}
              />

              <BakeasyLinkButton
                label={t('FORGOT_PASSOWRD')}
                style={{}}
                onClick={openForgotPasswordDialog}
              />
            </Grid>

            <BakeasyButton
              fullWidth={true}
              label={t('SIGN_IN')}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                onSubmit(emailId, password);
              }}
            />
            <Grid container marginTop={2} justifyContent="flex-end">
              <Typography fontSize="0.7rem">
                {t('VERSION')}: {env.VERSION}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};
